import { useCallback, useEffect, useMemo, useRef, useState, type SetStateAction } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { WorkoutService } from '../../../../lib/workoutService';
import { supabase } from '../../../../lib/supabase';
import type { WorkoutPlan, WorkoutSummary } from '../../../../types/dataModel';
import {
  clearPlanBuilderDraft,
  loadPlanBuilderDraft,
  savePlanBuilderDraft,
  type StoredPlanBuilderDraft,
  type StoredPlanDay,
} from '../utils/planBuilderDraftStorage';
import {
  cloneDays,
  compactAllDays,
  countProgrammedDays,
  countWorkoutSlots,
  dayHasWorkouts,
  daysEqual,
  daysFromPlan,
  emptyDays,
  flattenSlots,
  type PlanDay,
  sumEstimatedMinutes,
} from '../utils/planBuilderDays';

export type PlanBuilderMode = 'create' | 'view' | 'edit';
export type PlanAssignMode = 'add' | 'replace';

export type PlanBuilderToastAction =
  | { type: 'assign'; title: string }
  | { type: 'remove'; title: string }
  | { type: 'rest'; title: string }
  | { type: 'trim'; title: string }
  | { type: 'paste'; title: string; subtitle?: string }
  | { type: 'saved'; title: string }
  | { type: 'error'; title: string; subtitle?: string };

export interface PlanBuilderToastState {
  action: PlanBuilderToastAction;
  onUndo?: () => void;
}

function serializeDays(days: PlanDay[]): StoredPlanDay[] {
  return days.map((day) => ({
    workouts: day.workouts.map((slot) => ({
      workoutId: slot.workoutId,
      planWorkoutId: slot.planWorkoutId ?? null,
      orderInDay: slot.orderInDay,
    })),
  }));
}

function deserializeDays(stored: StoredPlanDay[]): PlanDay[] {
  return stored.map((day) => ({
    workouts: day.workouts.map((slot) => ({
      workoutId: slot.workoutId,
      planWorkoutId: slot.planWorkoutId ?? null,
      orderInDay: slot.orderInDay,
    })),
  }));
}

export interface UseTrainerWorkoutPlanBuilderStateOptions {
  isNewPlan: boolean;
  planId: string | undefined;
  trainerId: string | undefined;
  initialMode: PlanBuilderMode;
  navigate: NavigateFunction;
  locationState?: unknown;
  onModeChange?: (mode: PlanBuilderMode) => void;
}

export function useTrainerWorkoutPlanBuilderState({
  isNewPlan,
  planId,
  trainerId,
  initialMode,
  navigate,
  locationState,
  onModeChange,
}: UseTrainerWorkoutPlanBuilderStateOptions) {
  const [loading, setLoading] = useState(!isNewPlan);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<PlanBuilderMode>(isNewPlan ? 'create' : initialMode);
  const [planName, setPlanNameState] = useState('');
  const [nameError, setNameErrorState] = useState<string | null>(null);
  const [nameErrorSeq, setNameErrorSeq] = useState(0);
  const [difficulty, setDifficulty] = useState<WorkoutPlan['difficulty']>('intermediate');

  const setNameError = useCallback((message: string | null) => {
    setNameErrorState(message);
    if (message) setNameErrorSeq((n) => n + 1);
  }, []);

  const setPlanName = useCallback((value: SetStateAction<string>) => {
    setNameError(null);
    setPlanNameState(value);
  }, [setNameError]);
  const [totalDays, setTotalDays] = useState(7);
  const [days, setDays] = useState<PlanDay[]>(() => emptyDays(7));
  const [originalName, setOriginalName] = useState('');
  const [originalDifficulty, setOriginalDifficulty] =
    useState<WorkoutPlan['difficulty']>('intermediate');
  const [originalTotalDays, setOriginalTotalDays] = useState(7);
  const [originalDays, setOriginalDays] = useState<PlanDay[]>(() => emptyDays(7));

  const [selectedDayIndices, setSelectedDayIndices] = useState<Set<number>>(new Set());
  const [assignTargets, setAssignTargets] = useState<number[]>([]);
  const [assignMode, setAssignMode] = useState<PlanAssignMode>('add');
  const [assignReplaceIndex, setAssignReplaceIndex] = useState<number | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [trainerWorkouts, setTrainerWorkouts] = useState<WorkoutSummary[]>([]);
  const [loadingWorkouts, setLoadingWorkouts] = useState(false);

  const [pasteMode, setPasteMode] = useState(false);
  const [copySourceDayIndex, setCopySourceDayIndex] = useState<number | null>(null);
  const [clipboard, setClipboard] = useState<WorkoutSummary | null>(null);
  const [pasteCount, setPasteCount] = useState(0);
  const [pasteHintOffDays, setPasteHintOffDays] = useState<Set<number>>(new Set());

  const [undoSnapshot, setUndoSnapshot] = useState<PlanDay[] | null>(null);
  const [toast, setToast] = useState<PlanBuilderToastState | null>(null);
  const [hintDismissed, setHintDismissed] = useState(
    () => sessionStorage.getItem('twp_hint_dismissed') === '1',
  );

  const [pendingTrim, setPendingTrim] = useState<{
    targetDays: number;
    lostIndices: number[];
  } | null>(null);

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccessToast, setSaveSuccessToast] = useState<string | null>(null);

  const [showTemplateUpdateDialog, setShowTemplateUpdateDialog] = useState(false);
  const [activeInstances, setActiveInstances] = useState<
    Awaited<ReturnType<typeof WorkoutService.getActiveInstancesForTemplate>>
  >([]);
  const [selectedInstanceIds, setSelectedInstanceIds] = useState<Set<string>>(new Set());
  const [pendingSave, setPendingSave] = useState<{
    name: string;
    totalDays: number;
    difficulty?: WorkoutPlan['difficulty'];
    days: PlanDay[];
  } | null>(null);

  const returnStateHandled = useRef(false);

  const loadTrainerWorkouts = useCallback(async () => {
    setLoadingWorkouts(true);
    try {
      const rows = await WorkoutService.getWorkoutsSummary();
      const owned = trainerId ? rows.filter((w) => w.createdBy === trainerId) : rows;
      setTrainerWorkouts(owned);
    } catch (err) {
      console.error('Load trainer workouts:', err);
    } finally {
      setLoadingWorkouts(false);
    }
  }, [trainerId]);

  const hydrateDaysWithSummaries = useCallback(async (dayRows: PlanDay[]) => {
    const ids = Array.from(
      new Set(
        dayRows.flatMap((day) => day.workouts.map((slot) => slot.workoutId)).filter(Boolean),
      ),
    );
    if (!ids.length) return dayRows;
    try {
      const summaries = await WorkoutService.getWorkoutsSummaryByIds(ids);
      const byId = new Map(summaries.map((s) => [s.id, s]));
      return dayRows.map((day) => ({
        workouts: day.workouts.map((slot) =>
          byId.has(slot.workoutId) ? { ...slot, workout: byId.get(slot.workoutId)! } : slot,
        ),
      }));
    } catch (err) {
      console.error('Hydrate plan day summaries:', err);
      return dayRows;
    }
  }, []);

  const applyStoredDraft = useCallback(
    (draft: StoredPlanBuilderDraft) => {
      setMode(draft.mode);
      onModeChange?.(draft.mode);
      setPlanName(draft.planName);
      if (draft.difficulty) setDifficulty(draft.difficulty);
      setTotalDays(draft.totalDays);
      setDays(deserializeDays(draft.days));
      if (draft.originalName !== undefined) {
        setOriginalName(draft.originalName);
        if (draft.originalDifficulty) setOriginalDifficulty(draft.originalDifficulty);
        setOriginalTotalDays(draft.originalTotalDays ?? draft.totalDays);
        setOriginalDays(deserializeDays(draft.originalDays ?? draft.days));
      } else {
        setOriginalName('');
        setOriginalTotalDays(7);
        setOriginalDays(emptyDays(draft.totalDays));
      }
      setError(null);
    },
    [onModeChange],
  );

  const isEditable = mode === 'create' || mode === 'edit';

  useEffect(() => {
    if (isNewPlan) {
      const draft = loadPlanBuilderDraft('new');
      if (draft) {
        applyStoredDraft(draft);
        clearPlanBuilderDraft();
        void hydrateDaysWithSummaries(deserializeDays(draft.days)).then(setDays);
      }
      setLoading(false);
      return;
    }
    if (!planId) {
      setError('Workout plan ID is required');
      setLoading(false);
      return;
    }
    const st = locationState as {
      restorePlanDraft?: boolean;
      reopenAssignTargets?: number[];
    } | null;
    const shouldRestoreDraft =
      st?.restorePlanDraft === true || (st?.reopenAssignTargets?.length ?? 0) > 0;
    if (shouldRestoreDraft) {
      const draft = loadPlanBuilderDraft(planId);
      if (draft) {
        applyStoredDraft(draft);
        clearPlanBuilderDraft();
        setLoading(false);
        void hydrateDaysWithSummaries(deserializeDays(draft.days)).then((hydrated) => {
          setDays(hydrated);
          if (draft.originalDays) {
            void hydrateDaysWithSummaries(deserializeDays(draft.originalDays)).then(setOriginalDays);
          }
        });
        return;
      }
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const plan = await WorkoutService.getWorkoutPlanViewById(planId);
        if (cancelled) return;
        if (!plan) {
          setError('Workout plan not found');
          return;
        }
        const loadedDays = daysFromPlan(plan);
        setPlanName(plan.name);
        setDifficulty(plan.difficulty ?? 'intermediate');
        setTotalDays(plan.totalNoOfDays);
        setDays(loadedDays);
        setOriginalName(plan.name);
        setOriginalDifficulty(plan.difficulty ?? 'intermediate');
        setOriginalTotalDays(plan.totalNoOfDays);
        setOriginalDays(cloneDays(loadedDays));
        setError(null);
      } catch (err) {
        console.error('Load workout plan:', err);
        if (!cancelled) setError('Failed to load workout plan');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isNewPlan, planId, locationState, applyStoredDraft, hydrateDaysWithSummaries]);

  useEffect(() => {
    if (returnStateHandled.current) return;
    const st = locationState as {
      reopenAssignTargets?: number[];
      returnMode?: PlanBuilderMode;
      restorePlanDraft?: boolean;
    } | null;
    if (!st?.reopenAssignTargets?.length && !st?.restorePlanDraft) return;
    returnStateHandled.current = true;
    if (st.returnMode) {
      setMode(st.returnMode);
      onModeChange?.(st.returnMode);
    }
    if (st.reopenAssignTargets?.length) {
      setAssignMode('add');
      setAssignReplaceIndex(null);
      setAssignTargets(st.reopenAssignTargets);
      setDrawerOpen(true);
      void loadTrainerWorkouts();
    }
  }, [locationState, loadTrainerWorkouts, onModeChange]);

  const showActionToast = useCallback((action: PlanBuilderToastAction, snapshot?: PlanDay[]) => {
    if (snapshot) {
      const snap = cloneDays(snapshot);
      setUndoSnapshot(snap);
      setToast({
        action,
        onUndo: () => {
          setDays(cloneDays(snap));
          setUndoSnapshot(null);
          setToast(null);
        },
      });
    } else {
      setToast({ action });
    }
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const programmedDays = useMemo(() => countProgrammedDays(days), [days]);
  const totalWorkoutSlots = useMemo(() => countWorkoutSlots(days), [days]);
  const restDays = totalDays - programmedDays;
  const totalMins = useMemo(() => sumEstimatedMinutes(days), [days]);

  const isDirty = useMemo(() => {
    if (isNewPlan) {
      return planName.trim() !== '' || totalDays !== 7 || days.some(dayHasWorkouts);
    }
    return (
      planName.trim() !== originalName.trim() ||
      difficulty !== originalDifficulty ||
      totalDays !== originalTotalDays ||
      !daysEqual(days, originalDays)
    );
  }, [
    isNewPlan,
    planName,
    difficulty,
    totalDays,
    days,
    originalName,
    originalDifficulty,
    originalTotalDays,
    originalDays,
  ]);

  const weeks = useMemo(() => {
    const indices = Array.from({ length: totalDays }, (_, i) => i);
    const result: { weekNum: number; startDay: number; endDay: number; indices: number[] }[] = [];
    for (let i = 0; i < indices.length; i += 7) {
      const chunk = indices.slice(i, i + 7);
      result.push({
        weekNum: Math.floor(i / 7) + 1,
        startDay: chunk[0] + 1,
        endDay: chunk[chunk.length - 1] + 1,
        indices: chunk,
      });
    }
    return result;
  }, [totalDays]);

  const requestModeChange = useCallback(
    (next: PlanBuilderMode) => {
      if (mode === 'edit' && isDirty && next === 'view') {
        return 'confirm-discard' as const;
      }
      setMode(next);
      onModeChange?.(next);
      return 'ok' as const;
    },
    [mode, isDirty, onModeChange],
  );

  const dismissHint = useCallback(() => {
    setHintDismissed(true);
    try {
      sessionStorage.setItem('twp_hint_dismissed', '1');
    } catch {
      // ignore
    }
  }, []);

  const clearSelection = useCallback(() => setSelectedDayIndices(new Set()), []);

  const toggleDaySelection = useCallback((dayIndex: number) => {
    setSelectedDayIndices((prev) => {
      const next = new Set(prev);
      if (next.has(dayIndex)) next.delete(dayIndex);
      else next.add(dayIndex);
      return next;
    });
  }, []);

  const openDrawerForDays = useCallback(
    (indices: number[], mode: PlanAssignMode = 'add', replaceIndex: number | null = null) => {
      if (!indices.length) return;
      setPasteMode(false);
      setCopySourceDayIndex(null);
      clearSelection();
      setAssignMode(mode);
      setAssignReplaceIndex(mode === 'replace' ? replaceIndex : null);
      setAssignTargets(indices);
      setDrawerOpen(true);
      if (!trainerWorkouts.length) void loadTrainerWorkouts();
    },
    [clearSelection, trainerWorkouts.length, loadTrainerWorkouts],
  );

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setAssignTargets([]);
    setAssignMode('add');
    setAssignReplaceIndex(null);
  }, []);

  const assignWorkoutToTargets = useCallback(
    (workout: WorkoutSummary) => {
      if (!assignTargets.length) return;
      setDays((prev) => {
        const next = cloneDays(prev);
        if (assignMode === 'replace' && assignReplaceIndex != null) {
          const dayIndex = assignTargets[0];
          if (dayIndex >= 0 && dayIndex < next.length) {
            const day = next[dayIndex];
            if (assignReplaceIndex >= 0 && assignReplaceIndex < day.workouts.length) {
              const existing = day.workouts[assignReplaceIndex];
              day.workouts[assignReplaceIndex] = {
                workoutId: workout.id,
                planWorkoutId: existing.planWorkoutId ?? null,
                orderInDay: existing.orderInDay,
                workout,
              };
            }
          }
        } else {
          for (const idx of assignTargets) {
            if (idx >= 0 && idx < next.length) {
              const day = next[idx];
              day.workouts.push({
                workoutId: workout.id,
                orderInDay: day.workouts.length + 1,
                workout,
              });
            }
          }
        }
        return next;
      });
      closeDrawer();
    },
    [assignTargets, assignMode, assignReplaceIndex, closeDrawer],
  );

  const removeWorkoutSlot = useCallback(
    (dayIndex: number, slotIndex: number) => {
      setDays((prev) => {
        const next = cloneDays(prev);
        if (dayIndex < 0 || dayIndex >= next.length) return next;
        const day = next[dayIndex];
        if (slotIndex < 0 || slotIndex >= day.workouts.length) return next;
        day.workouts.splice(slotIndex, 1);
        next[dayIndex] = compactAllDays([day])[0];
        return next;
      });
    },
    [],
  );

  const bulkMarkRest = useCallback(() => {
    if (!selectedDayIndices.size) return;
    const snapshot = cloneDays(days);
    setDays((prev) => {
      const next = cloneDays(prev);
      for (const idx of Array.from(selectedDayIndices)) {
        if (idx >= 0 && idx < next.length) next[idx] = { workouts: [] };
      }
      return next;
    });
    showActionToast({ type: 'rest', title: 'Marked as rest' }, snapshot);
    clearSelection();
  }, [selectedDayIndices, days, showActionToast, clearSelection]);

  const enterPasteMode = useCallback(
    (sourceDayIndex: number, slotIndex: number) => {
      const src = days[sourceDayIndex]?.workouts[slotIndex];
      if (!src?.workout) return;
      setPasteMode(true);
      setCopySourceDayIndex(sourceDayIndex);
      setClipboard(src.workout);
      setPasteCount(0);
      setPasteHintOffDays(new Set());
      clearSelection();
      setDrawerOpen(false);
    },
    [days, clearSelection],
  );

  const pasteToDay = useCallback(
    (dayIndex: number) => {
      if (!pasteMode || !clipboard) return;
      setDays((prev) => {
        const next = cloneDays(prev);
        if (dayIndex >= 0 && dayIndex < next.length) {
          const day = next[dayIndex];
          day.workouts.push({
            workoutId: clipboard.id,
            orderInDay: day.workouts.length + 1,
            workout: clipboard,
          });
        }
        return next;
      });
      setPasteCount((c) => c + 1);
      setPasteHintOffDays((prev) => new Set(prev).add(dayIndex));
    },
    [pasteMode, clipboard],
  );

  const clearPasteHintOff = useCallback((dayIndex: number) => {
    setPasteHintOffDays((prev) => {
      if (!prev.has(dayIndex)) return prev;
      const next = new Set(prev);
      next.delete(dayIndex);
      return next;
    });
  }, []);

  const endPaste = useCallback((_withToast?: boolean) => {
    setPasteMode(false);
    setCopySourceDayIndex(null);
    setClipboard(null);
    setPasteCount(0);
    setPasteHintOffDays(new Set());
  }, []);

  const applyTotalDays = useCallback((n: number) => {
    const clamped = Math.min(84, Math.max(1, n));
    setDays((prev) => {
      if (clamped === prev.length) return prev;
      if (clamped > prev.length) {
        return [...prev, ...emptyDays(clamped - prev.length)];
      }
      return prev.slice(0, clamped);
    });
    setTotalDays(clamped);
  }, []);

  const requestTotalDaysChange = useCallback(
    (n: number) => {
      const clamped = Math.min(84, Math.max(1, n));
      if (clamped >= totalDays) {
        applyTotalDays(clamped);
        return;
      }
      const lostIndices: number[] = [];
      for (let i = clamped; i < totalDays; i++) {
        if (dayHasWorkouts(days[i])) lostIndices.push(i);
      }
      if (lostIndices.length === 0) {
        applyTotalDays(clamped);
        return;
      }
      setPendingTrim({ targetDays: clamped, lostIndices });
    },
    [totalDays, days, applyTotalDays],
  );

  const confirmTrim = useCallback(() => {
    if (!pendingTrim) return;
    const snapshot = cloneDays(days);
    applyTotalDays(pendingTrim.targetDays);
    const removedCount = totalDays - pendingTrim.targetDays;
    setPendingTrim(null);
    showActionToast(
      { type: 'trim', title: `${removedCount} day${removedCount === 1 ? '' : 's'} removed` },
      snapshot,
    );
  }, [pendingTrim, days, totalDays, applyTotalDays, showActionToast]);

  const cancelTrim = useCallback(() => setPendingTrim(null), []);

  const checkWorkoutPlanNameExists = useCallback(
    async (name: string, excludePlanId?: string) => {
      try {
        const allPlans = await WorkoutService.getWorkoutPlans();
        const trimmed = name.trim();
        if (!trimmed) return false;
        return allPlans.some(
          (p) =>
            p.name.trim().toLowerCase() === trimmed.toLowerCase() &&
            p.id !== excludePlanId &&
            p.createdBy === trainerId,
        );
      } catch {
        return false;
      }
    },
    [trainerId],
  );

  const syncSchedule = useCallback(async (id: string, before: PlanDay[], after: PlanDay[]) => {
    const beforeSlots = flattenSlots(compactAllDays(before));
    const afterSlots = flattenSlots(compactAllDays(after));

    const beforeById = new Map(
      beforeSlots
        .filter((slot) => slot.planWorkoutId)
        .map((slot) => [slot.planWorkoutId as string, slot]),
    );
    const afterById = new Map(
      afterSlots
        .filter((slot) => slot.planWorkoutId)
        .map((slot) => [slot.planWorkoutId as string, slot]),
    );

    for (const [planWorkoutId] of Array.from(beforeById.entries())) {
      if (!afterById.has(planWorkoutId)) {
        const { error } = await supabase.from('workouts_in_workoutplan').delete().eq('id', planWorkoutId);
        if (error) throw error;
      }
    }

    for (const slot of afterSlots) {
      if (slot.planWorkoutId && beforeById.has(slot.planWorkoutId)) {
        const orig = beforeById.get(slot.planWorkoutId)!;
        if (orig.workoutId !== slot.workoutId) {
          const { error: delError } = await supabase
            .from('workouts_in_workoutplan')
            .delete()
            .eq('id', slot.planWorkoutId);
          if (delError) throw delError;
          await WorkoutService.createPlanWorkout({
            workoutPlanId: id,
            workoutId: slot.workoutId,
            dayNo: slot.dayNo,
            orderInDay: slot.orderInDay,
            skipSync: true,
          });
        } else if (orig.dayNo !== slot.dayNo || orig.orderInDay !== slot.orderInDay) {
          await WorkoutService.updatePlanWorkoutSlot(slot.planWorkoutId, {
            dayNo: slot.dayNo,
            orderInDay: slot.orderInDay,
          });
        }
      } else {
        await WorkoutService.createPlanWorkout({
          workoutPlanId: id,
          workoutId: slot.workoutId,
          dayNo: slot.dayNo,
          orderInDay: slot.orderInDay,
          skipSync: true,
        });
      }
    }

    await WorkoutService.syncSessionsForPlanAssignments(id);
  }, []);

  const executeSave = useCallback(
    async (
      saveData: {
        name: string;
        totalDays: number;
        difficulty?: WorkoutPlan['difficulty'];
        days: PlanDay[];
      },
      instanceIdsToSync: string[],
    ) => {
      if (!planId) return;
      setIsSaving(true);
      try {
        await WorkoutService.updateWorkoutPlan(planId, {
          name: saveData.name,
          totalNoOfDays: saveData.totalDays,
          difficulty: saveData.difficulty,
        });
        await syncSchedule(planId, originalDays, saveData.days);

        if (instanceIdsToSync.length > 0) {
          await WorkoutService.syncMultipleInstancesWithTemplate(instanceIdsToSync);
        }

        const plan = await WorkoutService.getWorkoutPlanViewById(planId);
        if (plan) {
          const loaded = daysFromPlan(plan);
          setPlanName(plan.name);
          setDifficulty(plan.difficulty ?? 'intermediate');
          setTotalDays(plan.totalNoOfDays);
          setDays(loaded);
          setOriginalName(plan.name);
          setOriginalDifficulty(plan.difficulty ?? 'intermediate');
          setOriginalTotalDays(plan.totalNoOfDays);
          setOriginalDays(cloneDays(loaded));
        }

        setShowTemplateUpdateDialog(false);
        setActiveInstances([]);
        setPendingSave(null);
        setSelectedInstanceIds(new Set());
        setMode('view');
        onModeChange?.('view');
        setSaveSuccessToast('Plan saved');
        clearPlanBuilderDraft();
      } catch (err) {
        console.error('Save plan:', err);
        setToast({
          action: { type: 'error', title: 'Failed to save plan', subtitle: 'Please try again.' },
        });
      } finally {
        setIsSaving(false);
      }
    },
    [planId, originalDays, syncSchedule, onModeChange],
  );

  const handleSave = useCallback(async () => {
    if (pasteMode) endPaste(false);
    const trimmed = planName.trim();
    if (!trimmed) return;

    const nameExists = await checkWorkoutPlanNameExists(trimmed, planId);
    if (nameExists) {
      setNameError(
        `A plan with the name "${trimmed}" already exists. Please choose a different name.`,
      );
      return;
    }

    if (!planId) return;

    const saveData = {
      name: trimmed,
      totalDays,
      difficulty,
      days: cloneDays(days),
    };

    try {
      const instances = await WorkoutService.getActiveInstancesForTemplate(planId);
      if (instances.length > 0) {
        setPendingSave(saveData);
        setActiveInstances(instances);
        setSelectedInstanceIds(new Set());
        setShowTemplateUpdateDialog(true);
        return;
      }
      await executeSave(saveData, []);
    } catch (err) {
      console.error('Save plan:', err);
    }
  }, [
    pasteMode,
    endPaste,
    planName,
    checkWorkoutPlanNameExists,
    planId,
    totalDays,
    difficulty,
    days,
    executeSave,
  ]);

  const handleCreate = useCallback(async () => {
    if (pasteMode) endPaste(false);
    const trimmed = planName.trim();
    if (!trimmed) return;

    const nameExists = await checkWorkoutPlanNameExists(trimmed);
    if (nameExists) {
      setNameError(
        `A plan with the name "${trimmed}" already exists. Please choose a different name.`,
      );
      return;
    }

    setIsSaving(true);
    try {
      const newPlan = await WorkoutService.createWorkoutPlan(
        {
          name: trimmed,
          description: '',
          totalNoOfDays: totalDays,
          difficulty,
          isPublic: false,
        },
        { returnMinimal: true },
      );

      const compacted = compactAllDays(days);
      for (let i = 0; i < compacted.length; i++) {
        const day = compacted[i];
        for (const slot of day.workouts) {
          await WorkoutService.createPlanWorkout({
            workoutPlanId: newPlan.id,
            workoutId: slot.workoutId,
            dayNo: i + 1,
            orderInDay: slot.orderInDay,
          });
        }
      }

      try {
        localStorage.setItem('trainer_dashboard_active_section', 'library');
        localStorage.setItem('trainer_dashboard_active_tab', 'workoutPlans');
      } catch {
        // ignore
      }
      const returnTo =
        typeof (locationState as { returnTo?: unknown } | null)?.returnTo === 'string'
          ? (locationState as { returnTo: string }).returnTo
          : null;
      if (returnTo) {
        navigate(returnTo, {
          state: { planCreated: true, planName: trimmed },
        });
      } else {
        navigate('/trainer-dashboard', {
          state: { planCreated: true, planName: trimmed },
        });
      }
      clearPlanBuilderDraft();
    } catch (err) {
      console.error('Create plan:', err);
      setToast({
        action: { type: 'error', title: 'Failed to create plan', subtitle: 'Please try again.' },
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    pasteMode,
    endPaste,
    planName,
    checkWorkoutPlanNameExists,
    totalDays,
    difficulty,
    days,
    navigate,
    locationState,
  ]);

  const resetToOriginal = useCallback(() => {
    setPlanName(originalName);
    setDifficulty(originalDifficulty);
    setTotalDays(originalTotalDays);
    setDays(cloneDays(originalDays));
    clearSelection();
    setPasteMode(false);
    setCopySourceDayIndex(null);
    setClipboard(null);
    setPasteCount(0);
  }, [originalName, originalDifficulty, originalTotalDays, originalDays, clearSelection]);

  const navigateCreateWorkout = useCallback(() => {
    const routeKey = isNewPlan ? 'new' : planId;
    if (!routeKey) return;

    const draft: StoredPlanBuilderDraft = {
      routeKey,
      mode,
      planName,
      difficulty,
      totalDays,
      days: serializeDays(days),
    };
    if (!isNewPlan) {
      draft.originalName = originalName;
      draft.originalDifficulty = originalDifficulty;
      draft.originalTotalDays = originalTotalDays;
      draft.originalDays = serializeDays(originalDays);
    }
    savePlanBuilderDraft(draft);

    const returnTo = isNewPlan
      ? '/trainer-dashboard/workout-plan/new'
      : `/trainer-dashboard/workout-plan/${planId}`;
    const targets = assignTargets.length
      ? assignTargets
      : selectedDayIndices.size
        ? Array.from(selectedDayIndices)
        : [0];
    navigate('/trainer-dashboard/workout/new', {
      state: {
        returnTo,
        returnMode: mode,
        reopenAssignTargets: targets,
        restorePlanDraft: true,
      },
    });
  }, [
    isNewPlan,
    planId,
    mode,
    planName,
    difficulty,
    totalDays,
    days,
    originalName,
    originalDifficulty,
    originalTotalDays,
    originalDays,
    assignTargets,
    selectedDayIndices,
    navigate,
  ]);

  return {
    loading,
    error,
    mode,
    setMode,
    planName,
    setPlanName,
    nameError,
    nameErrorSeq,
    difficulty,
    setDifficulty,
    totalDays,
    days,
    isEditable,
    isDirty,
    isSaving,
    programmedDays,
    totalWorkoutSlots,
    restDays,
    totalMins,
    weeks,
    selectedDayIndices,
    assignTargets,
    assignMode,
    assignReplaceIndex,
    drawerOpen,
    trainerWorkouts,
    loadingWorkouts,
    pasteMode,
    copySourceDayIndex,
    clipboard,
    pasteCount,
    pasteHintOffDays,
    hintDismissed,
    pendingTrim,
    toast,
    saveSuccessToast,
    setSaveSuccessToast,
    showTemplateUpdateDialog,
    setShowTemplateUpdateDialog,
    activeInstances,
    selectedInstanceIds,
    setSelectedInstanceIds,
    pendingSave,
    requestModeChange,
    dismissHint,
    clearSelection,
    toggleDaySelection,
    openDrawerForDays,
    closeDrawer,
    assignWorkoutToTargets,
    removeWorkoutSlot,
    bulkMarkRest,
    enterPasteMode,
    pasteToDay,
    clearPasteHintOff,
    endPaste,
    requestTotalDaysChange,
    confirmTrim,
    cancelTrim,
    dismissToast,
    resetToOriginal,
    handleSave,
    handleCreate,
    navigateCreateWorkout,
    executeSave,
    loadTrainerWorkouts,
  };
}

export type TrainerWorkoutPlanBuilderApi = ReturnType<typeof useTrainerWorkoutPlanBuilderState>;
